/**
 * Aufteilung eines Deals in Cent: Budget, Provisionssatz (WS-19), Agenturgebühr (Prozent
 * oder fest), manueller Split, provisionsfreie Positionen, Zusatzumsatz – mit Summenprobe.
 * Gespeicherte Beträge werden nie zurückgerechnet (WS-13).
 */
import { foldText } from "./countries.js";
import { computeInvoiceTotals, percentOf, type LineInput } from "./money.js";
import { DomainError, type Cents, type Finding } from "./types.js";

// ---------------------------------------------------------------------------
// Provisionssatz (WS-19, KZ-01, KZ-02)
// ---------------------------------------------------------------------------

export interface ResolvedCommission {
  /** Agenturanteil in Prozent */
  percent: number;
  source: "deal" | "talent" | "agency";
}

function validPercent(v: number | null | undefined): v is number {
  return typeof v === "number" && Number.isFinite(v) && v >= 0 && v <= 100;
}

/** Deal-Wert → Talent-Standard → Agentur-Standard. Ein Deal-Satz von 0 % gilt (KZ-02). */
export function resolveCommissionRate(input: { deal?: number | null; talent?: number | null; agency: number }): ResolvedCommission {
  if (validPercent(input.deal)) return { percent: input.deal, source: "deal" };
  if (validPercent(input.talent)) return { percent: input.talent, source: "talent" };
  if (!validPercent(input.agency)) throw new DomainError("INVALID_AGENCY_COMMISSION", String(input.agency));
  return { percent: input.agency, source: "agency" };
}

// ---------------------------------------------------------------------------
// Aufteilung
// ---------------------------------------------------------------------------

export type AgencyFee = { type: "percent"; percent: number } | { type: "fixed"; amountCents: Cents };

export interface SplitLine extends LineInput {
  /** 'performance' = Provisionsumsatz (keine Agenturgebühr darauf, KZ-07) */
  kind?: "fixed" | "performance";
}

export type BillingModel = "talent_invoices_brand" | "agency_invoices_brand";

export interface DealSplitInput {
  lines: readonly SplitLine[];
  commissionPercent: number;
  agencyFee?: AgencyFee | null;
  /** Festbetrag nur im ersten ausgewiesenen Monat (KZ-07) – false in Folgemonaten */
  applyFixedFee?: boolean;
  /** Individuell: eingetragene Beträge sind die Wahrheit (KZ-03) */
  manualSplit?: { talentCents: Cents; agencyCents: Cents } | null;
  /** Zusatzumsatz (Nutzungsrechte), eigener Strom (KZ-08) */
  extraRevenueCents?: Cents;
  billing: BillingModel;
}

export interface DealSplit {
  budgetCents: Cents;
  commissionFreeCents: Cents;
  talentShare: Cents;
  agencyShare: Cents;
  agencyFee: Cents;
  extraRevenueCents: Cents;
  extraAgencyShare: Cents;
  /** Netto-Management = Agenturanteil + Anteil am Zusatz + Gebühr (KZ-09) */
  managementNet: Cents;
  /** Summe netto, die die Marke insgesamt bezahlt = Budget + Zusatz + Gebühr */
  brandInvoiceNet: Cents;
  /** Rechnungen an die Marke je nach Abrechnungsweg */
  brandInvoices: { issuer: "talent" | "agency"; netCents: Cents }[];
  /** Auszahlung an das Talent, wenn die Agentur voll abrechnet */
  talentPayoutCents: Cents | null;
  sumCheck: boolean;
}

export function computeDealSplit(input: DealSplitInput): DealSplit {
  if (!validPercent(input.commissionPercent)) throw new DomainError("INVALID_COMMISSION");
  const pct = input.commissionPercent;
  const totals = computeInvoiceTotals(input.lines, { vatRatePercent: 0, commissionPercent: pct });
  const budget = totals.rawNetCents;
  const commissionFree = totals.lines.filter((l) => l.commissionFree).reduce((a, l) => a + l.lineTotalCents, 0);

  let talentShare: Cents;
  let agencyShare: Cents;
  let base = budget;
  if (input.manualSplit) {
    talentShare = input.manualSplit.talentCents;
    agencyShare = input.manualSplit.agencyCents;
    base = talentShare + agencyShare;
  } else {
    // Praxistest K-129: eine Rundung mit Wächter, Anforderung, Gutschrift, Kennzahlen und Creator-Portal (shareOfHonorar, 0075):
    // Agenturanteil = Satz auf die provisionspflichtige Summe, auf den Cent gerundet; Creator = Rest. Vorher rundete die Aufteilung
    // je Position den Creator-Anteil (KZ-05/ST-18) und lag dadurch einen Cent neben den anderen Stellen (950,03 statt 950,02 €).
    agencyShare = Math.round(((budget - commissionFree) * pct) / 100);
    talentShare = budget - agencyShare;
  }

  // KZ-07: Gebühr in Prozent nur auf Fix-Budget, nie auf Provisionsumsatz oder Auslagen
  let fee = 0;
  if (input.agencyFee?.type === "percent") {
    const fixedBase = input.lines
      .filter((l) => (l.kind ?? "fixed") === "fixed" && !l.commissionFree && l.quantity > 0 && l.unitPriceCents > 0)
      .reduce((a, l) => a + computeInvoiceTotals([l], { vatRatePercent: 0 }).rawNetCents, 0);
    fee = percentOf(input.manualSplit ? base : fixedBase, input.agencyFee.percent);
  } else if (input.agencyFee?.type === "fixed" && input.applyFixedFee !== false) {
    fee = input.agencyFee.amountCents;
  }

  const extra = input.extraRevenueCents ?? 0;
  const extraAgency = percentOf(extra, pct);
  const managementNet = agencyShare + extraAgency + fee;
  const brandInvoiceNet = base + extra + fee;

  const brandInvoices =
    input.billing === "talent_invoices_brand"
      ? [
          { issuer: "talent" as const, netCents: talentShare + (extra - extraAgency) },
          { issuer: "agency" as const, netCents: managementNet },
        ]
      : [{ issuer: "agency" as const, netCents: brandInvoiceNet }];
  const talentPayout = input.billing === "agency_invoices_brand" ? talentShare + (extra - extraAgency) : null;

  // Probe gegen die Positionen: ein manueller Split muss dieselbe Summe ergeben wie die Positionen (R-15)
  const sumCheck =
    (input.lines.length === 0 || base === budget) &&
    talentShare + agencyShare === base &&
    brandInvoices.reduce((a, b) => a + b.netCents, 0) === brandInvoiceNet &&
    talentShare + (extra - extraAgency) + managementNet === brandInvoiceNet;

  return {
    budgetCents: base,
    commissionFreeCents: commissionFree,
    talentShare,
    agencyShare,
    agencyFee: fee,
    extraRevenueCents: extra,
    extraAgencyShare: extraAgency,
    managementNet,
    brandInvoiceNet,
    brandInvoices,
    talentPayoutCents: talentPayout,
    sumCheck,
  };
}

/**
 * Monatsweise Aufteilung: ein fester Gebührenbetrag fällt nur im ersten Monat mit Betrag an,
 * eine prozentuale Gebühr in jedem Monat (KZ-07). Monate ohne manuellen Split fallen auf den
 * Prozentsatz zurück, nicht auf 0 (KZ-03).
 */
export function computeMonthlySplits(
  months: readonly { month: string; lines: readonly SplitLine[]; manualSplit?: { talentCents: Cents; agencyCents: Cents } | null }[],
  common: Omit<DealSplitInput, "lines" | "manualSplit" | "applyFixedFee">,
): { month: string; split: DealSplit }[] {
  let feeUsed = false;
  return months.map((m) => {
    const hasAmount = m.lines.some((l) => l.quantity > 0 && l.unitPriceCents > 0) || !!m.manualSplit;
    const applyFixedFee = hasAmount && !feeUsed;
    if (applyFixedFee) feeUsed = true;
    return { month: m.month, split: computeDealSplit({ ...common, lines: m.lines, manualSplit: m.manualSplit ?? null, applyFixedFee }) };
  });
}

// ---------------------------------------------------------------------------
// Gespeicherte Beträge (KZ-06, WS-13)
// ---------------------------------------------------------------------------

export type StoredAmounts =
  | { complete: true; coopBudgetNetCents: Cents; talentNetCents: Cents; agencyShareCents: Cents }
  | { complete: false; manualReview: true; missing: string[] };

/**
 * Kooperationsbudget, Creator-Anteil und Agenturanteil sind gespeichert und die Wahrheit.
 * Fehlt etwas, wird NICHT zurückgerechnet, sondern die manuelle Prüfung verlangt.
 */
export function storedInvoiceAmounts(input: {
  coopBudgetNetCents: Cents | null;
  talentNetCents: Cents | null;
  agencyShareCents?: Cents | null;
}): StoredAmounts {
  const missing: string[] = [];
  if (input.coopBudgetNetCents == null) missing.push("coopBudgetNetCents");
  if (input.talentNetCents == null) missing.push("talentNetCents");
  if (missing.length) return { complete: false, manualReview: true, missing };
  const coop = input.coopBudgetNetCents!;
  const talent = input.talentNetCents!;
  return { complete: true, coopBudgetNetCents: coop, talentNetCents: talent, agencyShareCents: input.agencyShareCents ?? coop - talent };
}

// ---------------------------------------------------------------------------
// Auslagen-Erkennung (KZ-04)
// ---------------------------------------------------------------------------

/** Positionen, deren Text nach Auslage aussieht, lösen eine Rückfrage aus (keine Sperre). */
export function detectExpenseLikeLines(lines: readonly SplitLine[], keywords: readonly string[]): Finding[] {
  const kws = keywords.map((k) => foldText(k)).filter(Boolean);
  const out: Finding[] = [];
  lines.forEach((l, i) => {
    if (l.commissionFree) return;
    const text = foldText(l.description);
    const hit = kws.find((k) => new RegExp(`(?<![\\p{L}])${k.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}`, "u").test(text));
    if (hit) out.push({ code: "LINE_LOOKS_LIKE_EXPENSE", severity: "question", params: { line: i + 1, keyword: hit } });
  });
  return out;
}

// ---------------------------------------------------------------------------
// Manuelle Buchungen und Kennzahlen (KZ-12, KZ-13)
// ---------------------------------------------------------------------------

/**
 * KZ-13: Eine manuelle Buchung zählt nur mit ihrem Agenturanteil (Betrag × eingefrorener Satz;
 * Gebühr zu 100 %; ohne Satz: Talent-Satz, sonst Agentur-Standard) und gar nicht, wenn ihr Deal
 * selbst Umsatz ausweist.
 */
export function manualBookingAgencyShare(input: {
  amountCents: Cents;
  kind: "coop" | "fee";
  frozenPercent?: number | null;
  talentPercent?: number | null;
  agencyDefaultPercent: number;
  dealHasOwnRevenue: boolean;
}): Cents {
  if (input.dealHasOwnRevenue) return 0;
  if (input.kind === "fee") return input.amountCents;
  const r = resolveCommissionRate({ deal: input.frozenPercent ?? null, talent: input.talentPercent ?? null, agency: input.agencyDefaultPercent });
  return percentOf(input.amountCents, r.percent);
}

/** KZ-12: Ein Deal = eine Deal-ID; eine Buchung ohne Deal zählt als eigener Deal. */
export function averageDeal(entries: readonly { dealId: string | null; bookingId: string; amountCents: Cents }[]): { deals: number; totalCents: Cents; averageCents: Cents } {
  const keys = new Set(entries.map((e) => e.dealId ?? `booking:${e.bookingId}`));
  const total = entries.reduce((a, e) => a + e.amountCents, 0);
  const deals = keys.size;
  return { deals, totalCents: total, averageCents: deals ? Math.round(total / deals) : 0 };
}
